import { useEffect, useState, useCallback, useRef } from "react";
import {
  collection,
  getDocs,
  writeBatch,
  doc,
  query,
  orderBy,
  onSnapshot,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "./firebase";

const PAGE_SIZE = 50;

async function importWordsIfEmpty() {
  const wordsCol = collection(db, "words");
  const snap = await getDocs(wordsCol);
  if (!snap.empty) return;

  const response = await fetch("/words.txt");
  const text = await response.text();
  const words = text.split("\n").map((w) => w.trim()).filter(Boolean);

  let batch = writeBatch(db);
  let count = 0;
  for (const word of words) {
    const ref = doc(db, "words", word);
    batch.set(ref, { word, display: word, elo: 1200, wins: 0, losses: 0, skips: 0, comparisons: 0 });
    count++;
    if (count === 500) { await batch.commit(); batch = writeBatch(db); count = 0; }
  }
  if (count > 0) await batch.commit();
}

export function useWords() {
  const [words, setWords] = useState([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    importWordsIfEmpty()
      .then(() => {
        const unsub = onSnapshot(query(collection(db, "words")), (snap) => {
          setWords(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setReady(true);
        });
        return unsub;
      })
      .catch((err) => { console.error(err); setError(err.message); });
  }, []);

  return { words, ready, error };
}

export function useLeaderboard() {
  // page1 is live via onSnapshot, extraPages are static fetches
  const [page1, setPage1] = useState([]);
  const [extraPages, setExtraPages] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const cursorRef = useRef(null);       // last Firestore doc for pagination
  const loadingRef = useRef(false);     // prevent concurrent loads

  useEffect(() => {
    const q = query(collection(db, "words"), orderBy("elo", "desc"), limit(PAGE_SIZE));
    const unsub = onSnapshot(q, (snap) => {
      setPage1(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      // Only set cursor from page1 if we haven't paginated yet
      if (cursorRef.current === null) {
        cursorRef.current = snap.docs[snap.docs.length - 1] ?? null;
        setHasMore(snap.docs.length === PAGE_SIZE);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore || !cursorRef.current) return;

    loadingRef.current = true;
    setLoadingMore(true);

    try {
      const q = query(
        collection(db, "words"),
        orderBy("elo", "desc"),
        startAfter(cursorRef.current),
        limit(PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const newDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Append new page
      setExtraPages((prev) => [...prev, newDocs]);

      // Advance cursor
      cursorRef.current = snap.docs[snap.docs.length - 1] ?? null;
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error("loadMore error:", err);
    } finally {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore]);

  const words = [...page1, ...extraPages.flat()];

  return { words, loading, loadingMore, hasMore, loadMore };
}
